import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true, // httpOnly cookie otomatik gönderilir
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/*
 * ÇİFT GÖNDERİM KORUMASI — uçuştaki aynı POST tekrarlanmaz.
 *
 * NEDEN BURADA, form form DEĞİL: canlıda mükerrer ödeme oluştu (21.08.2026,
 * aynı öğrenciye aynı dakikada iki kez 700 ₺) çünkü "Ödeme Al" formundaki
 * düğmede `loading` yoktu ve birinci isteğin sonucu gelmeden ikinci kez
 * tıklandı. Backend'de idempotency kontrolü YOK, yani iki tıklama iki KAYIT
 * demek ve ortaya gerçek bir finansal hata çıkıyor.
 *
 * Tarandığında 21 formda daha aynı eksik vardı. Hepsine tek tek state
 * eklemek hem 21 ayrı değişiklik hem de YARIN yazılacak formda yine
 * unutulacak bir kural demekti. Kök sebep tek: aynı istek uçarken ikincisi
 * gönderiliyor. Burada kesilince bugünküler de gelecektekiler de kapsanıyor.
 *
 * HATA DEĞİL, AYNI SONUÇ döndürülüyor: ikinci çağrı birincinin promise'ini
 * paylaşıyor. Reddetseydik kullanıcı başarılı bir işlemin ardından
 * "kaydedilemedi" hatası görürdü — kayıt oluşmuşken.
 *
 * Kapsam bilerek DAR:
 * - Yalnızca POST. PUT/DELETE doğaları gereği tekrarlanabilir (aynı sonuç),
 *   mükerrer KAYIT üreten tek metot POST.
 * - FormData hariç: `JSON.stringify(FormData)` her zaman `{}` döndürür, yani
 *   iki FARKLI dosya yüklemesi aynı imzaya düşer ve ikincisi sessizce
 *   yutulurdu.
 * - Auth uçları hariç: yenileme akışının kendi kilidi var (`isRefreshing`) ve
 *   giriş denemesini engellemek kullanıcıyı kilitler.
 *
 * Kilit yalnızca istek UÇARKEN geçerli — birinci istek bitince aynı POST
 * tekrar gönderilebilir. Yani veli gerçekten aynı gün aynı tutarı iki kez
 * öderse bu engellenmiyor, yalnızca çift tıklama engelleniyor.
 */
const ucustakiPostlar = new Map();
const DEDUPE_HARICI = ['/auth/refresh', '/auth/admin/login', '/auth/parent/login'];

const orijinalPost = api.post.bind(api);
api.post = (url, data, config) => {
  const formDataMi = typeof FormData !== 'undefined' && data instanceof FormData;
  if (formDataMi || DEDUPE_HARICI.some((u) => String(url).includes(u))) {
    return orijinalPost(url, data, config);
  }

  let imza;
  try {
    imza = `${url}|${JSON.stringify(data ?? null)}`;
  } catch {
    // Döngüsel referans vb. — imza üretilemiyorsa koruma uygulanmaz.
    return orijinalPost(url, data, config);
  }

  if (ucustakiPostlar.has(imza)) {
    return ucustakiPostlar.get(imza);
  }

  const istek = orijinalPost(url, data, config).finally(() => {
    ucustakiPostlar.delete(imza);
  });

  ucustakiPostlar.set(imza, istek);
  return istek;
};

let isRefreshing = false;
let pendingRequests = [];

const processQueue = (error) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  pendingRequests = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // /auth/refresh ve giris uclari yenileme akisinin disinda kalmali:
      // refresh'in kendisi 401 alirsa tekrar refresh denemek sonsuz donguye
      // girer, giris uclarindaki 401 ise "sifre yanlis" demektir, yenilenecek
      // bir oturum yoktur.
      //
      // /auth/me BILINCLI olarak bu listede DEGIL. Eskiden buradaydi ve
      // suresi dolmus ama yenilenebilir bir oturumla acilan uygulama, hic
      // yenileme denenmeden giris ekranina atiliyordu (ozellikle bilgisayar
      // kapatilip ertesi gun acildiginda). _retry bayragi burada da donguyu
      // engelliyor.
      if (
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/admin/login') ||
        originalRequest.url?.includes('/auth/parent/login')
      ) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        // SADECE gercek 401'de cikis yap. Yenileme istegi 429 (hiz siniri),
        // 5xx ya da ag hatasi aldiysa oturum hala gecerli olabilir; burada
        // /login'e atmak kullaniciyi bosuna disari atiyor. Dahasi kullanici
        // hemen tekrar giris denedigi icin bu, hiz sinirini besleyen kendi
        // kendini surdüren bir donguye donusuyordu (2026-08-05 canli olayi:
        // ofisteki herkes sirayla disari atildi ve geri giremedi).
        if (refreshError.response?.status === 401) {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
